// Traducción de la interfaz. El texto fuente (español) es la clave: t('Guardar')
// busca en el diccionario del idioma activo y, si no hay traducción, devuelve
// el texto original. Los valores dinámicos van como {0}, {1}...:
// t('Hola {0}', nombre).
import React, { createContext, useContext, useEffect, useState } from 'react';
import en from './locales/en.json';
import pt from './locales/pt.json';

const STORAGE_KEY = 'kota-language';

export const LANGUAGES = [
  { code: 'es', label: 'Español', flag: '🇨🇱' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' }
];

const DICTIONARIES = { es: {}, en, pt };

const readStoredLanguage = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return DICTIONARIES[stored] ? stored : 'es';
  } catch {
    return 'es';
  }
};

let currentLanguage = typeof window !== 'undefined' ? readStoredLanguage() : 'es';
const listeners = new Set();

const normalize = (text) => text.replace(/\s+/g, ' ').trim();

export const t = (text, ...args) => {
  if (typeof text !== 'string' || !text) return text;
  const dictionary = DICTIONARIES[currentLanguage];
  let result = text;
  if (currentLanguage !== 'es') {
    result = dictionary[text] ?? dictionary[normalize(text)] ?? text;
  }
  if (args.length === 0) return result;
  return result.replace(/\{(\d+)\}/g, (match, index) => (args[index] ?? match));
};

export const getLanguage = () => currentLanguage;

export const setLanguage = (code) => {
  if (!DICTIONARIES[code] || code === currentLanguage) return;
  currentLanguage = code;
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // almacenamiento no disponible: el idioma dura solo esta sesión
  }
  document.documentElement.lang = code;
  listeners.forEach((listener) => listener(code));
};

const I18nContext = createContext({ language: currentLanguage, setLanguage, t });

// Al cambiar de idioma se vuelve a montar el árbol hijo para que todos los
// textos se recalculen. Los datos del ERP viven en los providers de arriba y
// no se pierden.
export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(currentLanguage);

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
    listeners.add(setLanguageState);
    return () => listeners.delete(setLanguageState);
  }, []);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      <React.Fragment key={language}>{children}</React.Fragment>
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
